import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Chip, Divider, Alert, Snackbar, MenuItem, Tooltip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const formatRM = (n) => `RM ${Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DownlineManagement() {
  const { user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [form, setForm] = useState({ name: '', email: '', phone: '', icNumber: '', role: 'individual_agent' });

  const bump = () => setRefreshKey((k) => k + 1);
  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  // ─── Live data from db ─────────────────────────────────
  const downlineIds = useMemo(() => {
    if (!user?.id) return [];
    return db.users.getDownlineUserIds(user.id);
  }, [user, refreshKey]);

  const downlineUsers = useMemo(() => {
    return downlineIds.map((id) => db.users.getById(id)).filter(Boolean);
  }, [downlineIds, refreshKey]);

  // Compute per-agent stats from live cases
  const agentStats = useMemo(() => {
    const allCases = db.cases.getAll();
    const map = {};
    downlineUsers.forEach((u) => {
      const ownCases = allCases.filter((c) => c.referralOwnerId === u.id);
      const totalCases = ownCases.length;
      const approvedCases = ownCases.filter((c) => ['Approved', 'Disbursed'].includes(c.status));
      const totalCommission = ownCases.reduce((sum, c) => sum + (Number(c.commissionAmount) || 0), 0);
      map[u.id] = {
        totalCases,
        approvedCases: approvedCases.length,
        totalCommission,
      };
    });
    return map;
  }, [downlineUsers, refreshKey]);

  const tableRows = useMemo(
    () => downlineUsers.map((u) => ({
      id: u.id,
      name: u.name,
      email: u.email,
      referralCode: u.referralCode || '—',
      status: u.status === 'active' ? 'Active' : 'Inactive',
      totalCases: agentStats[u.id]?.totalCases || 0,
      approvedCases: agentStats[u.id]?.approvedCases || 0,
      totalCommission: agentStats[u.id]?.totalCommission || 0,
      joinedDate: u.joinDate,
      role: u.role,
      raw: u,
    })),
    [downlineUsers, agentStats]
  );

  // Top-level stats
  const stats = useMemo(() => {
    const activeAgents = downlineUsers.filter((u) => u.status === 'active').length;
    const totalCases = tableRows.reduce((s, r) => s + r.totalCases, 0);
    const totalEarnings = tableRows.reduce((s, r) => s + r.totalCommission, 0);
    return {
      totalDownline: downlineUsers.length,
      activeAgents,
      totalCases,
      totalEarnings,
    };
  }, [downlineUsers, tableRows]);

  // Top 6 earners for the chart
  const earningsBreakdown = useMemo(() => {
    const top = [...tableRows].sort((a, b) => b.totalCommission - a.totalCommission).slice(0, 6);
    return top.map((r) => ({
      name: r.name.length > 14 ? r.name.slice(0, 14) + '...' : r.name,
      personal: Math.round(r.totalCommission * 0.65),
      override: Math.round(r.totalCommission * 0.35),
    }));
  }, [tableRows]);

  // ─── Actions ───────────────────────────────────────────
  const handleAddAgent = () => {
    if (!form.name || !form.email || !form.phone) {
      showSnack('Please fill name, email, and phone', 'warning');
      return;
    }
    const newUser = db.users.create({
      name: form.name,
      email: form.email,
      phone: form.phone,
      icNumber: form.icNumber,
      role: form.role,
      referralLevel: null,
      tokenBalance: 0,
      totalReferrals: 0,
      status: 'active',
      joinDate: new Date().toISOString().slice(0, 10),
      referredBy: user?.id,
      password: 'demo123',
    });
    db.auditLogs.create({
      userId: user?.id,
      userName: user?.name,
      userRole: user?.role,
      action: 'create_downline_agent',
      entityType: 'user',
      entityId: newUser.id,
      description: `Created sub-agent ${newUser.name} under partner ${user?.name}`,
    });
    db.engine.notify(newUser.id, 'system', 'Welcome to iMoney',
      `Your ${form.role === 'partner' ? 'partner' : 'agent'} account has been created by ${user?.name}.`);
    showSnack(`Sub-agent ${newUser.name} created (${newUser.id})`);
    setForm({ name: '', email: '', phone: '', icNumber: '', role: 'individual_agent' });
    setAddDialogOpen(false);
    bump();
  };

  const handleToggleStatus = (row) => {
    const newStatus = row.status === 'Active' ? 'inactive' : 'active';
    db.users.update(row.id, { status: newStatus });
    db.auditLogs.create({
      userId: user?.id,
      userName: user?.name,
      userRole: user?.role,
      action: newStatus === 'inactive' ? 'deactivate_agent' : 'activate_agent',
      entityType: 'user',
      entityId: row.id,
      description: `${newStatus === 'inactive' ? 'Deactivated' : 'Activated'} sub-agent ${row.name}`,
    });
    showSnack(`${row.name} ${newStatus === 'inactive' ? 'deactivated' : 'activated'}`);
    bump();
  };

  // ─── Columns ───────────────────────────────────────────
  const agentColumns = [
    {
      field: 'name', headerName: 'Name',
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
        </Box>
      ),
    },
    { field: 'referralCode', headerName: 'Referral Code', width: 160 },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (row) => <StatusBadge status={row.status} />,
    },
    { field: 'totalCases', headerName: 'Total Cases', width: 110, align: 'center' },
    { field: 'approvedCases', headerName: 'Approved', width: 100, align: 'center' },
    {
      field: 'totalCommission', headerName: 'Commission', width: 150, align: 'right',
      renderCell: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700, color: SECONDARY }}>
          {formatRM(row.totalCommission)}
        </Typography>
      ),
    },
    { field: 'joinedDate', headerName: 'Joined', width: 120 },
    {
      field: 'actions', headerName: 'Actions', width: 130, sortable: false,
      renderCell: (row) => (
        <Box sx={{ display: 'flex', gap: 0.25 }}>
          <Tooltip title="View Cases">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetailUserId(row.id); }}>
              <VisibilityIcon sx={{ fontSize: 18, color: PRIMARY }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.status === 'Active' ? 'Deactivate' : 'Activate'}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleStatus(row); }}>
              {row.status === 'Active'
                ? <BlockIcon sx={{ fontSize: 18, color: '#C62828' }} />
                : <PlayCircleIcon sx={{ fontSize: 18, color: SECONDARY }} />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Detail dialog cases list
  const detailUser = detailUserId ? db.users.getById(detailUserId) : null;
  const detailUserCases = detailUserId ? db.cases.getByReferralOwnerId(detailUserId) : [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY }}>
            Downline Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your sub-agents and track their performance
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<PersonAddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ bgcolor: PRIMARY, fontWeight: 600, borderRadius: 2, '&:hover': { bgcolor: '#142A4A' } }}>
          Add Sub-Agent
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Downline" value={stats.totalDownline} icon={GroupIcon} color={PRIMARY} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Active Agents" value={stats.activeAgents} icon={VerifiedUserIcon} color={SECONDARY} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Downline Cases" value={stats.totalCases} icon={FolderSharedIcon} color="#1565C0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Earnings" value={formatRM(stats.totalEarnings)} icon={AccountBalanceWalletIcon} color="#7B1FA2" />
        </Grid>
      </Grid>

      {stats.totalDownline === 0 && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          No sub-agents yet. Click "Add Sub-Agent" to create your first downline user, or use User Management (Admin) to generate invite links.
        </Alert>
      )}

      {/* Table */}
      <DataTable columns={agentColumns} rows={tableRows} title="Sub-Agents" searchable pageSize={10} />

      {/* Earnings breakdown chart */}
      {earningsBreakdown.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 3, mt: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>
            Top 6 Earners — Commission Breakdown
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={earningsBreakdown}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RechartsTooltip formatter={(v) => formatRM(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="personal" name="Personal" stackId="a" fill={PRIMARY} radius={[0, 0, 0, 0]} />
              <Bar dataKey="override" name="Override" stackId="a" fill={SECONDARY} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Add Sub-Agent Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>Add Sub-Agent</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            Create a new downline agent under your partner account
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Full Name *" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Email *" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Phone *" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="IC Number" value={form.icNumber}
                onChange={(e) => setForm({ ...form, icNumber: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select size="small" label="Role" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <MenuItem value="individual_agent">Individual Agent</MenuItem>
                <MenuItem value="partner">Partner (Corporate)</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAgent}
            sx={{ borderRadius: 2, textTransform: 'none', bgcolor: PRIMARY }}>
            Create Sub-Agent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog — agent's cases */}
      <Dialog open={!!detailUserId} onClose={() => setDetailUserId(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ fontWeight: 700, fontSize: '1.25rem' }}>{detailUser?.name}</Box>
          <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', fontWeight: 400 }}>
            {detailUser?.email} · {detailUserCases.length} cases
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailUserCases.length === 0 ? (
            <Alert severity="info">No cases yet from this agent.</Alert>
          ) : (
            detailUserCases.map((c) => (
              <Box key={c.id} sx={{
                p: 2, mb: 1, borderRadius: 2, backgroundColor: '#F5F7FA',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <Box>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: PRIMARY }}>{c.id}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.applicantName}</Typography>
                  <Typography variant="caption" color="text.secondary">{c.productName} · {c.bank} · {formatRM(c.amount)}</Typography>
                </Box>
                <StatusBadge status={c.status} />
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailUserId(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
